/**
 * Der **Speicher als Gerät** (HM-122, ADR 0038 §3): eine Batterie, die ein
 * Gateway liest — bei EVCC, bei Venus OS, über SunSpec — und deren Werte eine
 * verabredete Bedeutung haben.
 *
 * **Dieselbe Bauweise wie die Wallbox** (`wallbox.ts`, ADR 0032 §2): ein
 * Vertrag über Schlüssel, kein Katalog. Ein Ladezustand hat keine
 * OBIS-Kennzahl; was `soc` heißt, bedeutet das, und was anders heißt, wird
 * angezeigt und nicht gedeutet (Regel 9). Gedeutet wird **in der API** beim
 * Abbilden (`storageStateOf`), nicht in der Oberfläche — sonst läse Mobile
 * einen Ladezustand anders als Web.
 *
 * **Zwei der Schlüssel sind Zählwerke, einer ist eine signierte Leistung.**
 * `energy-charged` und `energy-discharged` tragen die Rollen Bezug und
 * Einspeisung: Über den Anlagevorschlag (HM-058) wird daraus ein
 * Zweirichtungszähler mit Messwerk im Gerät (HM-112) — der Speicherzähler, den
 * die Hausvorlagen in ihren Steckplatz „Speicher" nehmen. `power` ist **eine**
 * Leistung mit Vorzeichen, positiv beim Laden, negativ beim Entladen — dieselbe
 * Konvention wie am Netzzähler (OBIS 16.7.0), damit die Momentanwerte keinen
 * Sonderfall brauchen. Zwei getrennte, vorzeichenlose Leistungen ließen sich
 * nie zu einer Rolle `power_total` binden.
 *
 * **Nichts hier ist schreibbar.** Ein Speicher wird gelesen; wer ihn einmal
 * steuert (Ladung aus dem Netz, Entladesperre), bekommt einen eigenen Vertrag
 * wie der Ladepunkt (ADR 0036) und nicht ein `read_write` an diesen Schlüsseln.
 *
 * Reine Regeln: ohne Datenbank, ohne Uhr, ohne I/O.
 */
import type { RegisterRole } from "../meter/meter-register.js";
import type { Unit } from "../common/units.js";
import type { DatapointAccess } from "./datapoint.js";

/**
 * Der Gerätetyp, für den dieser Vertrag zuerst galt: der **Speicher aus EVCC**.
 * Der Schlüssel nennt die Quelle, nicht die Geräteklasse (ADR 0032, Nachtrag):
 * Ein Speicher, den ein Gateway über Venus OS oder SunSpec 802 liest, bekommt
 * einen eigenen Eintrag in `STORAGE_DEVICE_TYPES` — dieselbe Deutung, ein
 * anderer Absender.
 */
export const EVCC_BATTERY_DEVICE_TYPE = "evcc_battery";

/**
 * Die **Batterie hinter einem Victron GX** (HM-129): ein BMS oder ein Shunt,
 * gelesen über den MQTT-Broker von Venus OS. Anders als EVCC meldet sie in der
 * Regel geladene und entladene Energie — und damit einen Speicherzähler.
 */
export const VICTRON_BATTERY_DEVICE_TYPE = "victron_battery";

/** Alle Gerätetypen, für die `STORAGE_DATAPOINTS` gilt. */
export const STORAGE_DEVICE_TYPES = [EVCC_BATTERY_DEVICE_TYPE, VICTRON_BATTERY_DEVICE_TYPE] as const;

export function isStorageDeviceType(deviceType: string): boolean {
  return (STORAGE_DEVICE_TYPES as readonly string[]).includes(deviceType);
}

export interface StorageDatapointSpec {
  readonly key: string;
  readonly name: string;
  readonly unit: Unit | null;
  readonly access: DatapointAccess;
  readonly role: RegisterRole | null;
}

export const STORAGE_DATAPOINTS = {
  /** Anteil 0…1, nicht Prozent — wie jede `ratio` dieses Produkts. */
  soc: { key: "soc", name: "Ladezustand", unit: "ratio", access: "read", role: null },
  /** Signiert: positiv lädt, negativ entlädt. */
  power: {
    key: "power",
    name: "Leistung (+ Laden, − Entladen)",
    unit: "W",
    access: "read",
    role: "power_total",
  },
  energyCharged: {
    key: "energy-charged",
    name: "Geladen gesamt",
    unit: "Wh",
    access: "read",
    role: "energy_import_total",
  },
  energyDischarged: {
    key: "energy-discharged",
    name: "Entladen gesamt",
    unit: "Wh",
    access: "read",
    role: "energy_export_total",
  },
} as const satisfies Readonly<Record<string, StorageDatapointSpec>>;

export type StorageDatapointName = keyof typeof STORAGE_DATAPOINTS;

/**
 * Ein Wert, den die Oberfläche zeigt. `datapointId` ist `null`, wenn der
 * Speicher den Datenpunkt nicht gemeldet hat — dann lässt die Oberfläche die
 * Zeile weg, statt „nicht gemeldet" zu schreiben (HM-041).
 */
export interface StorageField<T> {
  readonly value: T | null;
  readonly datapointId: string | null;
}

export interface StorageState {
  /** Anteil 0…1. */
  readonly soc: StorageField<number>;
  /** Die gemeldete Leistung mit Vorzeichen. */
  readonly powerW: StorageField<number>;
  /** Aus dem Vorzeichen: was gerade in die Batterie geht — nie negativ. */
  readonly chargePowerW: number | null;
  /** Aus dem Vorzeichen: was gerade herauskommt — nie negativ. */
  readonly dischargePowerW: number | null;
  readonly energyChargedWh: StorageField<number>;
  readonly energyDischargedWh: StorageField<number>;
  /** Der jüngste Messzeitpunkt über alle Felder — `null`, solange nichts kam. */
  readonly measuredAt: Date | null;
}

export interface StorageDatapointSource {
  readonly id: string;
  readonly key: string;
  readonly valueType: "number" | "string";
}

export interface StorageValueSource {
  readonly datapointId: string;
  readonly value: number;
  readonly measuredAt: Date;
}

/**
 * Der Zustand eines Speichers aus seinem Katalog und den zuletzt gemeldeten
 * Werten — gedeutet über den Schlüssel, nie über die Reihenfolge. Ein
 * gemeldeter Datenpunkt ohne Wert ist ein Feld mit `value: null` und gesetzter
 * `datapointId`; ein Ladezustand außerhalb von 0…1 ist keiner und wird `null`.
 */
export function storageStateOf(
  datapoints: readonly StorageDatapointSource[],
  values: readonly StorageValueSource[],
): StorageState {
  const byKey = new Map(datapoints.map((point) => [point.key, point]));
  const valueById = new Map(values.map((value) => [value.datapointId, value]));

  let measuredAt: Date | null = null;
  const field = <T>(
    name: StorageDatapointName,
    decode: (raw: number) => T | null,
  ): StorageField<T> => {
    const point = byKey.get(STORAGE_DATAPOINTS[name].key);
    if (point === undefined || point.valueType !== "number") {
      return { value: null, datapointId: null };
    }
    const sample = valueById.get(point.id);
    if (sample !== undefined && (measuredAt === null || sample.measuredAt > measuredAt)) {
      measuredAt = sample.measuredAt;
    }
    return { value: sample === undefined ? null : decode(sample.value), datapointId: point.id };
  };
  const plain = (raw: number): number | null => (Number.isFinite(raw) ? raw : null);

  const powerW = field("power", plain);
  return {
    soc: field("soc", (raw) => (raw >= 0 && raw <= 1 ? raw : null)),
    powerW,
    chargePowerW: powerW.value === null ? null : Math.max(powerW.value, 0),
    dischargePowerW: powerW.value === null ? null : Math.max(-powerW.value, 0),
    energyChargedWh: field("energyCharged", plain),
    energyDischargedWh: field("energyDischarged", plain),
    measuredAt,
  };
}
